// utils_sdp.ts — Parsing SDP, forçage codec, comparaison.

import { ExtractedCodecs } from './types_media';

// ─── Compteur de log SDP ──────────────────────────────────────────────────────

let _sdpLogCounter = 0;
export const nextSdpLogId = (): number => ++_sdpLogCounter;

// ─── Capacités codec navigateur ───────────────────────────────────────────────

export const getSupportedVideoCodecNames = (): string[] => {
  if (!RTCRtpSender.getCapabilities) return [];
  return (RTCRtpSender.getCapabilities('video')?.codecs ?? [])
    .map(c => c.mimeType.split('/')[1])
    .filter((v, i, a) => a.indexOf(v) === i);
};

export const getSupportedAudioCodecNames = (): string[] => {
  if (!RTCRtpSender.getCapabilities) return [];
  return (RTCRtpSender.getCapabilities('audio')?.codecs ?? [])
    .map(c => c.mimeType.split('/')[1])
    .filter((v, i, a) => a.indexOf(v) === i);
};

// ─── Parsing SDP ──────────────────────────────────────────────────────────────
// Implémentation unique partagée par parseSdpCodec et extractCodecFromSdp.

const parseSdpCodecInternal = (sdp: string): {
  videoCodec:   string | null;
  videoProfile: string | null;
  audioCodec:   string | null;
  payloadType:  string | null;
} => {
  const lines = sdp.split(/\r?\n/);
  let inVideo = false, inAudio = false;
  let videoCodec: string | null = null, videoProfile: string | null = null;
  let audioCodec: string | null = null, payloadType:  string | null = null;

  for (const line of lines) {
    if      (line.startsWith('m=video')) {
      inVideo = true; inAudio = false;
      payloadType = line.split(' ').slice(3)[0] ?? null;
    }
    else if (line.startsWith('m=audio')) { inVideo = false; inAudio = true; }
    else if (line.startsWith('m='))      { inVideo = false; inAudio = false; }

    if (inVideo && payloadType && line.startsWith('a=rtpmap:') && line.includes(`:${payloadType} `)) {
      const m = line.match(/a=rtpmap:\d+ ([^/]+)/);
      if (m) videoCodec = m[1];
    }
    if (inVideo && payloadType && line.startsWith('a=fmtp:') && line.includes(`:${payloadType} `)) {
      const m = line.match(/profile-level-id=([0-9a-fA-F]{6})/i);
      if (m) videoProfile = m[1];
    }
    if (inAudio && !audioCodec && line.startsWith('a=rtpmap:')) {
      const m = line.match(/a=rtpmap:\d+ ([^/]+)/);
      if (m) audioCodec = m[1];
    }
  }

  return { videoCodec, videoProfile, audioCodec, payloadType };
};

/** Résumé compact pour les logs SDP : { video?, profile?, audio? } */
export const parseSdpCodec = (
  sdp: string
): { video?: string; profile?: string; audio?: string } => {
  const { videoCodec, videoProfile, audioCodec } = parseSdpCodecInternal(sdp);
  return {
    ...(videoCodec   ? { video:   videoCodec   } : {}),
    ...(videoProfile ? { profile: videoProfile } : {}),
    ...(audioCodec   ? { audio:   audioCodec   } : {}),
  };
};

/** Forme étendue avec payloadType, utilisée pour la négociation codec. */
export const extractCodecFromSdp = (sdp: string): ExtractedCodecs =>
  parseSdpCodecInternal(sdp);

// ─── Filtrage sections SDP ────────────────────────────────────────────────────

const filterSdpSection = (section: string, keepPts: string[]): string => {
  if (keepPts.length === 0) return section;
  const mLineReplaced = section.replace(
    /^(m=\S+ \d+ \S+)([\d ]+)/m,
    (_match, prefix) => `${prefix} ${keepPts.join(' ')}`
  );
  return mLineReplaced.split(/\r?\n/).filter(line => {
    const pt = (
      line.match(/^a=rtpmap:(\d+)/) ||
      line.match(/^a=fmtp:(\d+)/)   ||
      line.match(/^a=rtcp-fb:(\d+)/)
    )?.[1];
    return !(pt && !keepPts.includes(pt));
  }).join('\r\n');
};

// ─── Forçage codec SDP côté régie ─────────────────────────────────────────────

/**
 * Ordre de préférence des profils H.264, du moins au plus coûteux à décoder :
 * Constrained Baseline (42e0) → Main (4d00) → High (640) → le reste.
 *
 * Constrained Baseline est délibérément en tête : pas de CABAC ni de trames B,
 * donc le décodage côté ffmpeg (microservice de diffusion) est nettement moins
 * cher — c'est lui qui reprend ce flux derrière MediaMTX.
 *
 * Exporté pour rester l'UNIQUE source de vérité : forceSdpCodecs() filtre le SDP
 * avec, et setCodecPreferences() (api_whip) ordonne les codecs avec le même
 * barème. Les deux mécanismes doivent concorder, sans quoi le profil réellement
 * négocié dépend de l'ordre par défaut du navigateur.
 */
export const h264ProfilePriority = (profile: string, preferredPrefix?: string | null): number => {
  const p = (profile || '').toLowerCase();
  // Profil choisi dans les réglages d'encodage de la régie : passe devant le barème.
  if (preferredPrefix && p.startsWith(preferredPrefix)) return -1;
  return p.startsWith('42e0') ? 0 : p.startsWith('4d00') ? 1 : p.startsWith('640') ? 2 : 3;
};

/**
 * Impose un débit de DÉPART à l'encodeur vidéo, par les extensions `x-google-*`.
 *
 * Le problème qu'elle résout : Chrome ouvre toute publication autour de 300 kb/s et
 * ne monte qu'en sondant le lien. Les premières secondes sont donc molles — et sur
 * un ENREGISTREMENT, ces secondes-là sont dans le fichier pour toujours. Constaté
 * le 25/08/2026 : « ça démarre en petite définition puis ça s'améliore ».
 *
 * Ces trois paramètres ne sont pas standard : ils sont propres à libwebrtc, donc
 * compris de Chrome et d'Edge, et purement et simplement ignorés ailleurs. Les
 * poser ne casse donc rien là où ils ne servent pas.
 *
 * `min` retient l'encodeur de redescendre au premier hoquet, `start` lui dit où
 * commencer, `max` reste gouverné par `maxBitrate` du sender — on ne le fixe pas
 * ici pour ne pas avoir deux vérités.
 *
 * ⚠️ `minKbps` est OPTIONNEL depuis le 27/08/2026, et l'omettre n'est pas anodin :
 * un plancher est une exigence, pas une préférence. Sur un lien qui ne peut pas
 * l'honorer, il est soupçonné d'empêcher l'allocation initiale — donc la naissance
 * même de l'encodeur (`encoderImplementation` absent, zéro image encodée). Les
 * angles le passent désormais sans plancher ; le programme, lui, garde le sien s'il
 * en veut un. Voir `angles-iphone-2026-08-27.md`.
 */
export const withStartBitrate = (sdp: string, startKbps: number, minKbps?: number): string =>
  sdp.split('\r\n').map(ligne => {
    if (!ligne.startsWith('a=fmtp:')) return ligne;
    // Seules les lignes qui décrivent un codec VIDÉO nous intéressent : les poser
    // sur l'audio n'aurait aucun effet, sinon d'embrouiller la lecture d'une SDP.
    if (!/(packetization-mode|profile-level-id|apt=)/.test(ligne)) return ligne;
    if (ligne.includes('x-google-start-bitrate')) return ligne;
    const plancher = minKbps && minKbps > 0 ? `;x-google-min-bitrate=${minKbps}` : '';
    return `${ligne};x-google-start-bitrate=${startKbps}${plancher}`;
  }).join('\r\n');

/** Débit audio Opus visé pour le programme (bits/s) : musique stéréo, pas de la voix de téléphone. */
export const WHIP_OPUS_BITRATE = 192_000;

/**
 * Rend l'Opus du programme STÉRÉO et à haut débit.
 *
 * Constat du 2026-10-16 : MediaMTX annonçait `a=fmtp:96 sprop-stereo=0` sur le flux publié
 * par la régie — Opus mono, au débit par défaut de Chrome (une trentaine de kb/s, conçu
 * pour la voix) — alors que la mire, qui ne passe pas par WebRTC, était parfaite.
 *
 * Chrome règle son encodeur d'après le fmtp de la description DISTANTE (ce que le
 * récepteur accepte) : on ajoute donc `stereo=1; sprop-stereo=1; maxaveragebitrate=…` à la
 * réponse de MediaMTX avant de la poser. Les paramètres déjà présents sont conservés,
 * ceux qu'on pose écrasent leurs homologues. Sans ligne Opus, le SDP est rendu tel quel.
 */
export const withStereoOpus = (
  sdp: string,
  bitrate: number = WHIP_OPUS_BITRATE,
  opts: { stereo?: boolean; fec?: boolean; dtx?: boolean } = {},
): string => {
  const { stereo = true, fec = false, dtx = false } = opts;
  const eol = sdp.includes('\r\n') ? '\r\n' : '\n';
  const lines = sdp.split(eol);
  const opusPts = lines
    .map(l => l.match(/^a=rtpmap:(\d+) opus\/48000/i)?.[1])
    .filter((pt): pt is string => !!pt);
  if (!opusPts.length) return sdp;

  const wanted: Record<string, string> = {
    stereo: stereo ? '1' : '0', 'sprop-stereo': stereo ? '1' : '0', maxaveragebitrate: String(bitrate),
    maxplaybackrate: '48000', useinbandfec: fec ? '1' : '0', usedtx: dtx ? '1' : '0',
  };
  const merge = (existing: string): string => {
    const kv = new Map<string, string>();
    existing.split(';').map(x => x.trim()).filter(Boolean).forEach(x => {
      const [k, ...v] = x.split('=');
      kv.set(k.trim(), v.join('=').trim());
    });
    Object.entries(wanted).forEach(([k, v]) => kv.set(k, v));
    return Array.from(kv.entries()).map(([k, v]) => (v === '' ? k : `${k}=${v}`)).join(';');
  };

  const out: string[] = [];
  const done = new Set<string>();
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const fm = line.match(/^a=fmtp:(\d+) (.*)$/);
    if (fm && opusPts.includes(fm[1])) {
      out.push(`a=fmtp:${fm[1]} ${merge(fm[2])}`);
      done.add(fm[1]);
      continue;
    }
    out.push(line);
    const rm = line.match(/^a=rtpmap:(\d+) opus\/48000/i);
    if (rm) {
      // Pas de fmtp pour ce type de charge : on en crée un juste après le rtpmap.
      const hasFmtp = lines.some(l => l.startsWith(`a=fmtp:${rm[1]} `));
      if (!hasFmtp) { out.push(`a=fmtp:${rm[1]} ${merge('')}`); done.add(rm[1]); }
    }
  }
  return out.join(eol);
};

export const forceSdpCodecs = (sdp: string, preferredProfilePrefix?: string | null): string =>
  sdp.split(/(?=m=)/).map(section => {
    if (section.startsWith('m=video')) {
      const ptProfiles: { pt: string; profile: string }[] = [];
      const re = /a=rtpmap:(\d+) H264\/\d+/gi;
      let m: RegExpExecArray | null;
      while ((m = re.exec(section)) !== null) {
        const pt = m[1];
        const fm = section.match(new RegExp(`a=fmtp:${pt} .*profile-level-id=([0-9a-fA-F]{6})`));
        ptProfiles.push({ pt, profile: fm?.[1]?.toLowerCase() ?? '' });
      }
      ptProfiles.sort((a, b) => h264ProfilePriority(a.profile, preferredProfilePrefix) - h264ProfilePriority(b.profile, preferredProfilePrefix));
      return ptProfiles.length ? filterSdpSection(section, [ptProfiles[0].pt]) : section;
    }
    if (section.startsWith('m=audio')) {
      const opusPts: string[] = [];
      const re = /a=rtpmap:(\d+) (opus|PCMU|PCMA|G722)\/\d+/gi;
      let m: RegExpExecArray | null;
      while ((m = re.exec(section)) !== null) {
        m[2].toLowerCase() === 'opus' ? opusPts.unshift(m[1]) : opusPts.push(m[1]);
      }
      return opusPts.length ? filterSdpSection(section, [opusPts[0]]) : section;
    }
    return section;
  }).join('');

// ─── Forçage codec SDP côté device ────────────────────────────────────────────

export const forceSpecificCodecInSdp = (
  sdp: string,
  codec: string,
  profilePrefix: string
): string => {
  const lines = sdp.split('\r\n');
  let inVideo = false;
  const pts: { pt: string; profile: string; score: number }[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if      (line.startsWith('m=video')) inVideo = true;
    else if (line.startsWith('m='))      inVideo = false;

    if (inVideo) {
      const rm = line.match(new RegExp(`^a=rtpmap:(\\d+) ${codec}\\/\\d+`, 'i'));
      if (rm) {
        const pt = rm[1]; let profile = '';
        for (let j = i + 1; j < Math.min(i + 5, lines.length); j++) {
          const pm = lines[j].match(
            new RegExp(`a=fmtp:${pt}.*profile-level-id=([0-9a-fA-F]{6})`, 'i')
          );
          if (pm) { profile = pm[1].toLowerCase(); break; }
        }
        const score =
          profile === profilePrefix.toLowerCase()          ? 0 :
          profile.startsWith(profilePrefix.toLowerCase())  ? 1 : 2;
        pts.push({ pt, profile, score });
      }
    }
  }

  if (!pts.length) return sdp;
  pts.sort((a, b) => a.score - b.score);
  const sel = pts[0].pt;

  return sdp.split(/(?=m=)/).map(section => {
    if (!section.startsWith('m=video')) return section;
    return filterSdpSection(section, [sel]);
  }).join('');
};

// ─── Comparaison SDP ──────────────────────────────────────────────────────────

export const sdpsHaveCodecMismatch = (
  sdp1: string | undefined,
  sdp2: string
): boolean => {
  if (!sdp1) return false;
  const c1 = parseSdpCodecInternal(sdp1);
  const c2 = parseSdpCodecInternal(sdp2);
  if (!c1.videoCodec || !c2.videoCodec) return false;
  if (c1.videoCodec !== c2.videoCodec)  return true;
  if (c1.videoProfile && c2.videoProfile && c1.videoProfile !== c2.videoProfile) return true;
  return false;
};
